/**
 * AOI identity — spec §F5.10 (draw AOI) in service of §E7 (the analysis params hash) and
 * §F6 (the dock's "same AOI or a redrawn one?").
 *
 * The §F5.10 ring is a list of vertices, and a list of vertices is a poor key. It is a new
 * array object on every drag frame, it is re-created wholesale when a permalink restores
 * it, and two rings that describe the same shape to five decimal places are still two
 * different arrays. Three things need a *stable* answer to "which AOI is this?":
 *
 * * **§E7's params hash.** An AOI job's cache entry keys on the parameters, and the
 *   parameters carry the ring. A ring that re-renders into a new array must not re-queue a
 *   90-second raster sweep.
 * * **the §F6 dock.** "This panel describes the AOI you drew" and "you have drawn a new
 *   one since" are different states, and telling them apart by array identity would call
 *   every re-render a redraw.
 * * **the §F5.6 legend and the result layers.** A result computed over one AOI keeps its
 *   provenance when the reader draws another.
 *
 * So an AOI carries an `id`: `sha1(ring rounded to 5 dp)`, first 10 hex characters.
 *
 * **Five decimal places** is ~1.1 m at the equator — finer than any ring a reader can draw
 * with a mouse, and the same rounding the permalink writes, so a shared AOI has the same id
 * for the recipient as it had for the sender. **Ten hex characters** is 40 bits: at the
 * handful of AOIs a session draws, a collision is not a consideration, and a short token is
 * one a person can compare in a URL or a log line.
 *
 * **sha-1, and synchronously.** `crypto.subtle.digest` is the platform's hash and it is
 * `async` — but `setAoi` is a zustand action called from a Leaflet click handler, and an
 * action that returned a promise would leave the store briefly holding an AOI with no
 * identity, which is the one state every consumer above must never see. The digest is
 * therefore computed here, in ~40 lines of the algorithm as published (FIPS 180-4). This is
 * **not** a security primitive: it names a polygon, it does not protect one.
 */
import { toLatLon, type PointLike } from './geo';

/** Decimal places every coordinate is rounded to before it is hashed or shared. */
export const AOI_PLACES = 5;

/** How much of the digest becomes the id. See the module note. */
export const AOI_ID_LENGTH = 10;

const SCALE = 10 ** AOI_PLACES;

/**
 * One coordinate at `AOI_PLACES`. The `+ 0` is not decoration: `Math.round(-0.000001 * 1e5)`
 * is `-0`, and `-0` and `0` format as `"-0.00000"` and `"0.00000"` — two ids for one ring
 * whose first vertex sits a metre either side of the Greenwich meridian.
 */
export function roundCoord(value: number): number {
  return Math.round(value * SCALE) / SCALE + 0;
}

/**
 * The canonical text of a ring — `lat,lon;lat,lon;…`, every number at `AOI_PLACES`.
 *
 * Fixed notation rather than `String(n)`: `1e-7` and `0.0000001` are the same number and
 * two different strings, and a hash cannot tell that they meant the same polygon.
 */
export function ringText(pts: readonly PointLike[]): string {
  return pts
    .map((p) => {
      const [lat, lon] = toLatLon(p);
      return `${roundCoord(lat).toFixed(AOI_PLACES)},${roundCoord(lon).toFixed(AOI_PLACES)}`;
    })
    .join(';');
}

/**
 * The AOI's stable identity — see the module note. Pure and total: any list of points has
 * one, including the empty list, so no caller has to guard before asking.
 */
export function ringId(pts: readonly PointLike[]): string {
  return sha1Hex(ringText(pts)).slice(0, AOI_ID_LENGTH);
}

/* ------------------------------------------------------------------ sha-1 (FIPS 180-4) */

function rotl(value: number, bits: number): number {
  return ((value << bits) | (value >>> (32 - bits))) >>> 0;
}

/** Lower-case hex of one 32-bit word, zero-padded to 8 characters. */
function hex32(word: number): string {
  return (word >>> 0).toString(16).padStart(8, '0');
}

/** sha-1 of `text` (UTF-8), as 40 lower-case hex characters. */
export function sha1Hex(text: string): string {
  const bytes = new TextEncoder().encode(text);
  // The message, `0x80`, zero padding, and a 64-bit big-endian bit length — to a multiple
  // of 64 bytes.
  const total = Math.ceil((bytes.length + 9) / 64) * 64;
  const block = new Uint8Array(total);
  block.set(bytes);
  block[bytes.length] = 0x80;
  const view = new DataView(block.buffer);
  const bits = bytes.length * 8;
  view.setUint32(total - 8, Math.floor(bits / 2 ** 32), false);
  view.setUint32(total - 4, bits >>> 0, false);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;
  const w = new Uint32Array(80);

  for (let offset = 0; offset < total; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 80; i++) {
      w[i] = rotl(w[i - 3]! ^ w[i - 8]! ^ w[i - 14]! ^ w[i - 16]!, 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;
    for (let i = 0; i < 80; i++) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      // Every term is below 2**32 and there are five of them, so the sum is exact in a
      // double long before `>>> 0` truncates it — no `Math.imul` dance needed.
      const temp = (rotl(a, 5) + (f >>> 0) + e + k + w[i]!) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  return hex32(h0) + hex32(h1) + hex32(h2) + hex32(h3) + hex32(h4);
}
